import { AtSign, Building, IdCard, Phone, ShieldCheck, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ROLE_LABELS, type UserRole } from '@/lib/constants/roles';
import { useAuthStore } from '@/lib/auth/store';
import { useUserDetails } from '../api';
import type { UserProfile as UserProfileType } from '../types';

export function UserProfilePage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const profile = useUserDetails(userId);

  return (
    <ScrollPage>
      <PageHeader
        title="Your profile"
        description="Account information and balances."
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardContent className="p-6">
            {profile.isLoading ? (
              <ProfileSkeleton />
            ) : profile.data ? (
              <ProfileDetails data={profile.data} role={role} />
            ) : (
              <p className="text-sm text-muted-foreground">Could not load your profile.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h3 className="text-sm font-semibold">Balances</h3>
            <BalanceRow
              icon={<Wallet className="h-4 w-4" />}
              label="Voice balance"
              value={profile.data?.credits}
              loading={profile.isLoading}
            />
            <BalanceRow
              icon={<Wallet className="h-4 w-4" />}
              label="TTS balance"
              value={profile.data?.ttsCredits}
              loading={profile.isLoading}
            />
            {profile.data?.planName && (
              <div className="rounded-md bg-muted/40 px-3 py-2">
                <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Plan
                </div>
                <div className="text-sm font-medium text-foreground">{profile.data.planName}</div>
              </div>
            )}
            {profile.data?.ttsPlanName && (
              <div className="rounded-md bg-muted/40 px-3 py-2">
                <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  TTS plan
                </div>
                <div className="text-sm font-medium text-foreground">{profile.data.ttsPlanName}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ScrollPage>
  );
}

function ProfileDetails({ data, role }: { data: UserProfileType; role: UserRole | null }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{data.name || data.username}</h2>
          <p className="text-sm text-muted-foreground">@{data.username}</p>
        </div>
        {role && <Badge variant="outline">{ROLE_LABELS[role]}</Badge>}
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field icon={<AtSign className="h-4 w-4" />} label="Email" value={data.emailid} />
        <Field icon={<Phone className="h-4 w-4" />} label="Phone" value={data.number} />
        <Field icon={<Building className="h-4 w-4" />} label="Company" value={data.company} />
        <Field
          icon={<IdCard className="h-4 w-4" />}
          label="User type"
          value={data.userType}
        />
        {data.parentType && (
          <Field
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Parent type"
            value={data.parentType}
          />
        )}
        {data.expiryDate && (
          <Field
            icon={<IdCard className="h-4 w-4" />}
            label="Validity"
            value={data.expiryDate}
          />
        )}
      </dl>
    </div>
  );
}

function Field({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div>
        <dt className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="text-sm font-medium text-foreground">{value ?? '—'}</dd>
      </div>
    </div>
  );
}

function BalanceRow({
  icon,
  label,
  value,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number;
  loading?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        {loading ? (
          <Skeleton className="mt-1 h-4 w-16" />
        ) : (
          <div className="text-sm font-semibold tabular-nums text-foreground">
            ₹{Number(value ?? 0).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-6 w-24" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}
